import { Button, Flex, Header } from '@driftt/dds'
import { Subnav } from '@driftt/tide-subnav'
import { showTransientNotification } from '@driftt/traffic-control'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import useSWR from 'swr'

import { getPlaybooks } from 'api/playbooks'
import Marketplace from 'components/PlaybooksTable/components/Marketplace'
import PlaybooksUserOnboardedModal from 'components/PlaybooksUserOnboardedModal'
import { APP_PAGE_TITLES } from 'constants/pageTitles'
import { CHAT_USER_PLAYBOOK_TYPES } from 'constants/types'
import useDocumentTitle from 'hooks/usePageTitle'
import { capitalizeFirstLetter, replaceWith } from 'utils'
import {
  orgHasPlaybookPriorityFeature,
  orgHasWorkspaceAccess,
  userHasAnyPlaybookAccess,
  userHasPlaybookPriorityAccess,
} from 'utils/access'
import { getSubNavNodes } from 'utils/getSubNavNodes'

import { PrioritizeModal, Table, TableHeader } from './components'
import PlaybookPriorityAccessWarning from './components/PlaybookPriorityAccessWarning'
import {
  PLAYBOOK_CATEGORIES,
  PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS,
} from './constants'
import { generateCacheKey } from './helpers'

export const DEFAULT_API_PARAMS = {
  category: null,
  includeMetrics: false,
  nameQuery: null,
  page: 0,
  pageSize: 25,
  onlyMine: false,
  sortDirection: 'asc',
  sortProperty: 'priority',
  filterEnabled: false,
}

const PATH_NAMES = {
  automation: 'automation',
  chat: 'chat',
  email: 'email',
  fastlane: 'Fastlane', // eslint-disable-line @typescript-eslint/camelcase
  sequence: 'sequence',
  meetings: 'meeting',
}

const noop = () => {}

function PlaybooksTableContainer({
  match,
  orgUsers,
  showTransientNotification,
  history,
}) {
  const [apiParams, setApiParams] = useState(DEFAULT_API_PARAMS)
  const [sidebarIsOpen, setSidebarIsOpen] = useState(true)
  const [showPrioritizedPlaybooks, setShowPrioritizedPlaybooks] =
    useState(false)
  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()

  useDocumentTitle(APP_PAGE_TITLES.PLAYBOOK)

  const playbookType = useMemo(() => {
    return replaceWith(
      R.pathOr('', ['params', 'type'], match),
      '-',
      '_'
    ).toUpperCase()
  }, [match])

  const initializeShowPriorityBanner = () =>
    playbookType === 'CHAT' &&
    // TODO: remove org-level priority feature check here once playbook priority has become global
    orgHasWorkspaceAccess() &&
    orgHasPlaybookPriorityFeature() &&
    !userHasPlaybookPriorityAccess()

  const [showPriorityAccessWarning, setShowPriorityAccessWarning] = useState(
    initializeShowPriorityBanner
  )

  const orgUsersById = useMemo(
    () => R.indexBy(R.prop('id'), orgUsers),
    [orgUsers]
  )
  const onChange = useCallback(
    (params, callback = noop) => {
      setApiParams((oldParams) => R.merge(oldParams, params))

      if (callback) {
        callback()
      }
    },
    [setApiParams]
  )

  const cacheKey = !!apiParams.category ? generateCacheKey(apiParams) : null

  const playbooksSWR = useSWR(
    cacheKey,
    () =>
      getPlaybooks(apiParams).then(({ data }) => {
        return {
          meta: data.meta,
          playbooks: data.playbooks,
        }
      }),
    {
      dedupingInterval: 300000,
    }
  )

  const SUB_NAV_NODES = getSubNavNodes()

  useEffect(() => {
    const isValidCategory = PLAYBOOK_CATEGORIES[playbookType] || false

    if (isValidCategory) {
      const defaults = PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS[playbookType][0]

      onChange({
        category: defaults.value,
        page: 0,
        sortDirection: defaults.sortProperties.dir,
        sortProperty: defaults.sortProperties.property,
      })
    }
  }, [playbookType, onChange])

  function togglePrioritiesModal() {
    setShowPrioritizedPlaybooks(!showPrioritizedPlaybooks)
  }

  if (
    !hasAnyPlaybookAccess &&
    !CHAT_USER_PLAYBOOK_TYPES.includes(playbookType)
  ) {
    return <Redirect to="/list" />
  }

  const pathname =
    PATH_NAMES[
      replaceWith(R.pathOr('chat', ['params', 'type'], match), '-', '_')
    ] || 'chat'
  const formattedPathname = capitalizeFirstLetter(pathname)

  const openPriorityPageOrModal = (type) =>
    userHasPlaybookPriorityAccess()
      ? history.push('/priority/' + type)
      : togglePrioritiesModal()

  const isFastLane = pathname.toLowerCase() === 'fastlane'

  return (
    <>
      <PlaybooksUserOnboardedModal />
      {hasAnyPlaybookAccess && (
        <Subnav
          title="Chatbot"
          nodes={SUB_NAV_NODES}
          open={sidebarIsOpen}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
      )}
      <Flex
        direction="column"
        fullWidth
        css={{ backgroundColor: '$gray2', overflow: 'scroll' }}
      >
        <Flex
          direction="row"
          fullWidth
          justifyContent="space-between"
          css={{ backgroundColor: '$white', padding: '$3' }}
        >
          <Header variant="h2">{`${formattedPathname} playbooks`}</Header>
          {isFastLane ? (
            <Button
              onClick={() => {
                history.push('/list/fastlane/create')
              }}
            >
              Create Fastlane playbook
            </Button>
          ) : (
            <Marketplace
              hasAnyPlaybookAccess={hasAnyPlaybookAccess}
              pathname={pathname}
            />
          )}
        </Flex>
        <Flex
          direction="column"
          css={{
            margin: '$2',
            boxShadow: '$1',
            width: 'calc(100% - 32px)',
            marginBottom: '180px',
          }}
        >
          {showPriorityAccessWarning && (
            <PlaybookPriorityAccessWarning
              onClose={() => setShowPriorityAccessWarning(false)}
            />
          )}
          <TableHeader
            onChange={onChange}
            playbookType={playbookType}
            openPriorityPageOrModal={openPriorityPageOrModal}
          />
          <Table
            playbookType={playbookType}
            playbooksSWR={playbooksSWR ?? {}}
            apiParams={apiParams}
            onChange={onChange}
            orgUsers={orgUsersById}
            showTransientNotification={showTransientNotification}
            isPrioritizedPlaybooksOpen={showPrioritizedPlaybooks}
            history={history}
          />
          {showPrioritizedPlaybooks && (
            <PrioritizeModal
              onChange={playbooksSWR.mutate}
              orgUsers={orgUsersById}
              showTransientNotification={showTransientNotification}
              togglePrioritiesModal={togglePrioritiesModal}
              playbookType={playbookType}
            />
          )}
        </Flex>
      </Flex>
    </>
  )
}

const mapStateToProps = ({ org }) => ({
  orgUsers: org.users,
})

const mapDispatchToProps = (dispatch) => ({
  showTransientNotification: (config) => {
    dispatch(showTransientNotification(config))
  },
})

const enhance = connect(mapStateToProps, mapDispatchToProps)

export default enhance(PlaybooksTableContainer)
